import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import MDEditor from "@uiw/react-md-editor";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "react-toastify";
import { getMDTReportsById, updateMDTReportContext } from "../services/mdtReportService";
import { extractMDTReportContext } from "../utils/reportUtils";

const ReportEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [value, setValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [reportObj, setReportObj] = useState<any>(null);

  useEffect(() => {
    if (!id) return;

    const fetchReport = async () => {
      try {
        const report = await getMDTReportsById(id);
        setReportObj(report);
        const parsed = extractMDTReportContext(report?.context);
        setValue(parsed.reportText);
      } catch (err) {
        console.error("Failed to fetch MDT report", err);
        toast.error("Failed to load MDT report");
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);

  const handleSave = async () => {
    if (!id) return;

    if (!value.trim()) {
      toast.warning("MDT report content cannot be empty");
      return;
    }

    try {
      const existingContext = reportObj?.context || {};
      let updatedContext: any;
      if (typeof existingContext === 'object' && existingContext !== null) {
        updatedContext = {
          ...existingContext,
          generation: value,
          mdt_report: value,
        };
      } else {
        updatedContext = {
          generation: value,
          mdt_report: value,
        };
      }

      await updateMDTReportContext(id, {
        context: updatedContext,
      });
      toast.success("MDT report saved successfully");
      navigate(`/reports/${id}`, {
        state: location.state
      });
    } catch (error) {
      console.error("Save failed", error);
      toast.error("Failed to save report edits");
    }    
  };

  if (loading) {
    return <div className="p-6 text-gray-600">Loading report for editing...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>

          <h1 className="text-2xl font-bold text-gray-900">
            Edit MDT Report #{id}
          </h1>
        </div>

        <button
          onClick={handleSave}
          className="btn-primary flex items-center space-x-2 disabled:opacity-50"
        >
          <Save className="h-4 w-4" /> 
          <span>Save Changes</span>
        </button>
      </div>

      {/* Editor */}
      <div className="card p-2" data-color-mode="light">
        <MDEditor
          value={value}
          onChange={(val: any) => setValue(val || "")}
          height={650}
          preview="edit"
        />
      </div>

      {/* Footer Medical Disclaimer */}
      <div className="pt-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between text-xs text-gray-500 gap-2">
        <span>
          AI-Assisted Clinical Decision Support — Multidisciplinary consultation document. Requires clinician review.
        </span>
        <Link
          to="/legal?tab=medical-disclaimer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-700 underline font-medium whitespace-nowrap"
        >
          Medical Disclaimer &rarr;
        </Link>
      </div>
    </div>
  );
};

export default ReportEdit;
